/**
 * La cave › Carte & verre (/cave/edition-groupee) — vue tabulaire de TOUTES les bouteilles
 * (domaine · cuvée · millésime · format) pour gérer en un coup d'œil les options
 * « Sur la carte » et « Au verre ». Filtres (couleur, domaine, en stock), sélection multiple
 * pour appliquer un choix groupé, et tout cocher/décocher par colonne.
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, X, Check, RefreshCw } from 'lucide-react';
import { caveApi } from '../../services/api';
import { Spinner, EmptyState, PageTitle, Button } from '../../components/ui/kit';
import { WineColorDot } from '../../components/wine/wine';
import { invalidateStock } from '../../services/refresh';
import { formatEuro } from '../../utils/format';
import { WINE_COLOR_DOTS, COLOR_FILTER_ORDER } from '../../constants';

const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function EditionGroupee() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [color, setColor] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sort, setSort] = useState({ key: 'domain', dir: 'asc' });
  const [flags, setFlags] = useState({});
  const [selected, setSelected] = useState(new Set());
  const { data, isLoading } = useQuery({ queryKey: ['stock'], queryFn: () => caveApi.stock({}) });

  const items = useMemo(() => {
    const rows = data?.rows || [];
    const out = [];
    rows.forEach((row) => (row.references || []).forEach((r) => out.push({
      ref_id: r.id, domain_name: row.domain_name || '—', cuvee_name: row.name, color: row.color,
      year: r.year || 'NM', format_label: r.format_label || r.format, quantity: r.quantity,
      price_table: r.price_table, on_wine_list: !!r.on_wine_list, by_glass: !!r.by_glass,
    })));
    return out.sort((a, b) => (a.domain_name + a.cuvee_name + a.year).localeCompare(b.domain_name + b.cuvee_name + b.year));
  }, [data]);

  useEffect(() => {
    const init = {};
    items.forEach((it) => { init[it.ref_id] = { on_wine_list: it.on_wine_list, by_glass: it.by_glass }; });
    setFlags(init);
  }, [items]);

  const filtered = useMemo(() => {
    const t = norm(q.trim());
    return items.filter((it) => {
      if (color && it.color !== color) return false;
      if (inStockOnly && it.quantity <= 0) return false;
      if (t && !norm(`${it.domain_name} ${it.cuvee_name} ${it.year}`).includes(t)) return false;
      return true;
    });
  }, [items, q, color, inStockOnly]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (key === 'price') return mul * ((a.price_table ?? -1) - (b.price_table ?? -1));
      if (key === 'stock') return mul * (a.quantity - b.quantity);
      return mul * `${a.domain_name} ${a.cuvee_name} ${a.year}`.localeCompare(`${b.domain_name} ${b.cuvee_name} ${b.year}`);
    });
    return arr;
  }, [filtered, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }));
  const sortIcon = (key) => (sort.key === key ? (sort.dir === 'asc' ? '▲' : '▼') : '');

  const persist = async (refIds, patch) => {
    if (!refIds.length) return false;
    setFlags((f) => { const next = { ...f }; refIds.forEach((id) => { next[id] = { ...next[id], ...patch }; }); return next; });
    try { await caveApi.bulkRefFlags({ reference_ids: refIds, ...patch }); invalidateStock(qc); return true; }
    catch (e) { toast.error('Échec de la mise à jour'); qc.invalidateQueries({ queryKey: ['stock'] }); return false; }
  };

  const toggle = (refId, key) => persist([refId], { [key]: !flags[refId]?.[key] });
  const setAll = (key, value) => persist(filtered.map((it) => it.ref_id), { [key]: value });
  const allOn = (key) => filtered.length > 0 && filtered.every((it) => flags[it.ref_id]?.[key]);

  const toggleSelect = (refId) => setSelected((s) => { const n = new Set(s); n.has(refId) ? n.delete(refId) : n.add(refId); return n; });
  const allSelected = filtered.length > 0 && filtered.every((it) => selected.has(it.ref_id));
  const toggleSelectAll = () => setSelected(() => (allSelected ? new Set() : new Set(filtered.map((it) => it.ref_id))));
  const applyToSelection = async (key, value) => { const n = selected.size; const ok = await persist([...selected], { [key]: value }); if (ok) toast.success(`${n} bouteille(s) mises à jour`); };
  const recomputeSelection = async () => {
    const ids = [...selected];
    try {
      const r = await caveApi.bulkRecompute(ids);
      qc.invalidateQueries({ queryKey: ['stock'] }); invalidateStock(qc);
      const upd = r.updated ?? 0; const skip = r.skipped ?? 0;
      toast.success(`${upd} recalculée(s)${skip ? ` · ${skip} ignorée(s) (prix forcé / sans achat)` : ''}`);
    } catch { toast.error('Recalcul impossible'); }
  };

  if (isLoading) return <div className="p-6"><Spinner /></div>;
  const selCount = selected.size;
  const onListCount = filtered.filter((it) => flags[it.ref_id]?.on_wine_list).length;
  const byGlassCount = filtered.filter((it) => flags[it.ref_id]?.by_glass).length;

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <PageTitle kicker="La cave" title="Carte & verre" />
      <p className="text-[13px] text-n600 mt-1 mb-4">
        {filtered.length} référence(s) · <span className="nums">{onListCount}</span> sur la carte · <span className="nums">{byGlassCount}</span> au verre
      </p>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-n500" />
          <input className="vd-input h-9 pl-8 w-64" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="edition-search" />
        </div>
        <select className="vd-input h-9 w-40" value={color} onChange={(e) => setColor(e.target.value)} data-testid="filter-color">
          <option value="">Toutes couleurs</option>
          {COLOR_FILTER_ORDER.map((c) => <option key={c} value={c}>{WINE_COLOR_DOTS[c]?.label || c}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-[13px] text-n700 cursor-pointer whitespace-nowrap">
          <input type="checkbox" checked={inStockOnly} onChange={(e) => setInStockOnly(e.target.checked)} className="accent-[#b68235] w-4 h-4" data-testid="filter-instock" />
          En stock seulement
        </label>
      </div>

      {/* Barre d'action pour la sélection multiple */}
      {selCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 vd-card p-2.5 bg-surface/60" data-testid="selection-bar">
          <span className="text-[13px] font-medium px-1">{selCount} sélectionnée(s)</span>
          <span className="text-n400">|</span>
          <Button className="h-8 text-[12px]" onClick={() => applyToSelection('on_wine_list', true)} data-testid="sel-wine-on"><Check className="w-3.5 h-3.5" /> Mettre sur la carte</Button>
          <Button className="h-8 text-[12px]" onClick={() => applyToSelection('on_wine_list', false)} data-testid="sel-wine-off">Retirer de la carte</Button>
          <Button className="h-8 text-[12px]" onClick={() => applyToSelection('by_glass', true)} data-testid="sel-glass-on"><Check className="w-3.5 h-3.5" /> Proposer au verre</Button>
          <Button className="h-8 text-[12px]" onClick={() => applyToSelection('by_glass', false)} data-testid="sel-glass-off">Retirer du verre</Button>
          <Button className="h-8 text-[12px]" onClick={recomputeSelection} data-testid="sel-recompute"><RefreshCw className="w-3.5 h-3.5" /> Recalculer les prix</Button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-n500 hover:text-ink" title="Annuler la sélection" data-testid="sel-clear"><X className="w-4 h-4" /></button>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState title="Aucune bouteille" description="Aucune référence ne correspond aux filtres." testId="edition-empty" />
      ) : (
        <div className="vd-card overflow-hidden">
          <table className="w-full text-[13.5px]" data-testid="edition-table">
            <thead>
              <tr className="text-left kicker">
                <th className="py-2.5 px-3 w-8"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="accent-[#b68235] w-4 h-4" data-testid="select-all" /></th>
                <th className="py-2.5 px-3 cursor-pointer select-none hover:text-ink" onClick={() => toggleSort('domain')} data-testid="sort-domain">Domaine {sortIcon('domain')}</th>
                <th className="py-2.5 px-3">Cuvée</th>
                <th className="py-2.5 px-3">Millésime</th>
                <th className="py-2.5 px-3">Format</th>
                <th className="py-2.5 px-3 text-right cursor-pointer select-none hover:text-ink" onClick={() => toggleSort('stock')} data-testid="sort-stock">Stock {sortIcon('stock')}</th>
                <th className="py-2.5 px-3 text-right cursor-pointer select-none hover:text-ink" onClick={() => toggleSort('price')} data-testid="sort-price">Service à table {sortIcon('price')}</th>
                <th className="py-2.5 px-2 text-center">
                  <div className="flex flex-col items-center gap-0.5"><span>Sur la carte</span>
                    <label className="flex items-center gap-1 text-[10px] font-normal normal-case text-n500 cursor-pointer">
                      <input type="checkbox" checked={allOn('on_wine_list')} onChange={(e) => setAll('on_wine_list', e.target.checked)} className="accent-[#b68235] w-3.5 h-3.5" data-testid="all-wine-list" /> tout</label>
                  </div>
                </th>
                <th className="py-2.5 px-2 text-center">
                  <div className="flex flex-col items-center gap-0.5"><span>Au verre</span>
                    <label className="flex items-center gap-1 text-[10px] font-normal normal-case text-n500 cursor-pointer">
                      <input type="checkbox" checked={allOn('by_glass')} onChange={(e) => setAll('by_glass', e.target.checked)} className="accent-[#b68235] w-3.5 h-3.5" data-testid="all-by-glass" /> tout</label>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((it) => (
                <tr key={it.ref_id} className={`hair-t hover:bg-surface/50 ${selected.has(it.ref_id) ? 'bg-gold100/40' : ''}`} data-testid={`edition-row-${it.ref_id}`}>
                  <td className="py-2 px-3"><input type="checkbox" checked={selected.has(it.ref_id)} onChange={() => toggleSelect(it.ref_id)} className="accent-[#b68235] w-4 h-4 cursor-pointer" data-testid={`select-${it.ref_id}`} /></td>
                  <td className="py-2 px-3 text-n600 whitespace-nowrap">{it.domain_name}</td>
                  <td className="py-2 px-3"><span className="flex items-center gap-2"><WineColorDot color={it.color} size={9} /> {it.cuvee_name}</span></td>
                  <td className="py-2 px-3 nums text-n600">{it.year}</td>
                  <td className="py-2 px-3 text-n600">{it.format_label}</td>
                  <td className="py-2 px-3 text-right nums">{it.quantity}</td>
                  <td className="py-2 px-3 text-right nums text-n700" data-testid={`price-${it.ref_id}`}>{formatEuro(it.price_table)}</td>
                  <td className="py-2 px-2 text-center"><input type="checkbox" checked={!!flags[it.ref_id]?.on_wine_list} onChange={() => toggle(it.ref_id, 'on_wine_list')} className="accent-[#b68235] w-4 h-4 cursor-pointer" data-testid={`wine-list-${it.ref_id}`} /></td>
                  <td className="py-2 px-2 text-center"><input type="checkbox" checked={!!flags[it.ref_id]?.by_glass} onChange={() => toggle(it.ref_id, 'by_glass')} className="accent-[#b68235] w-4 h-4 cursor-pointer" data-testid={`by-glass-${it.ref_id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
